import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  adminLoginRequest,
  adminLogoutRequest,
  adminMeRequest,
  adminTwoFactorChallengeRequest,
  adminTwoFactorConfirmRequest,
  adminTwoFactorSetupRequest,
} from "../../api/adminAuthApi";
import {
  AdminLoginRequest,
  AdminTwoFactorCodeRequest,
} from "../../models/RequestModels/AdminLoginRequest";
import {
  AdminAuthSuccessResponse,
  AdminLoginResponse,
  AdminTwoFactorSetupResponse,
  isAdminAuthSuccessResponse,
  isAdminTwoFactorRequiredResponse,
  isAdminTwoFactorSetupRequiredResponse,
} from "../../models/ResponseModels/AdminLoginResponse";
import { AdminUserResponse } from "../../models/ResponseModels/AdminUserResponse";
import {
  getToken,
  removeToken,
  saveToken,
} from "../../utils/helperFunctions/tokenStorage";
import {
  ApiRejectPayload,
  FieldErrors,
  getApiErrorPayload,
} from "../../utils/helperFunctions/apiErrorHelper";

export type AdminTwoFactorMode = "none" | "setup" | "challenge";

interface AdminAuthState {
  user: AdminUserResponse | null;
  token: string | null;
  tokenExpiresAt: string | null;

  pendingToken: string | null;
  pendingTokenExpiresAt: string | null;

  setupToken: string | null;
  setupTokenExpiresAt: string | null;

  challengeToken: string | null;
  challengeTokenExpiresAt: string | null;

  twoFactorMode: AdminTwoFactorMode;
  setupSecret: string | null;
  setupOtpAuthUri: string | null;
  recoveryCodes: string[];

  loading: boolean;
  initialized: boolean;
  error: string | null;
  fieldErrors: FieldErrors;
}

const initialState: AdminAuthState = {
  user: null,
  token: null,
  tokenExpiresAt: null,

  pendingToken: null,
  pendingTokenExpiresAt: null,

  setupToken: null,
  setupTokenExpiresAt: null,

  challengeToken: null,
  challengeTokenExpiresAt: null,

  twoFactorMode: "none",
  setupSecret: null,
  setupOtpAuthUri: null,
  recoveryCodes: [],

  loading: false,
  initialized: false,
  error: null,
  fieldErrors: {},
};

const applyReject = (state: AdminAuthState, action: any) => {
  const payload = action.payload as ApiRejectPayload | undefined;

  state.loading = false;
  state.initialized = true;
  state.error = payload?.message || "Request failed.";
  state.fieldErrors = payload?.fieldErrors || {};
};

const resetSessionState = (state: AdminAuthState) => {
  state.user = null;
  state.token = null;
  state.tokenExpiresAt = null;

  state.pendingToken = null;
  state.pendingTokenExpiresAt = null;

  state.setupToken = null;
  state.setupTokenExpiresAt = null;

  state.challengeToken = null;
  state.challengeTokenExpiresAt = null;

  state.twoFactorMode = "none";
  state.setupSecret = null;
  state.setupOtpAuthUri = null;
  state.recoveryCodes = [];
};

const applyAuthSuccess = (
  state: AdminAuthState,
  response: AdminAuthSuccessResponse,
  saveImmediately: boolean,
) => {
  state.user = response.user;
  state.tokenExpiresAt = response.token_expires_at || null;

  state.setupToken = null;
  state.setupTokenExpiresAt = null;
  state.challengeToken = null;
  state.challengeTokenExpiresAt = null;

  state.twoFactorMode = "none";
  state.setupSecret = null;
  state.setupOtpAuthUri = null;

  state.loading = false;
  state.initialized = true;
  state.error = null;
  state.fieldErrors = {};

  if (response.recovery_codes?.length) {
    state.pendingToken = response.token;
    state.pendingTokenExpiresAt = response.token_expires_at || null;
    state.recoveryCodes = response.recovery_codes;
    state.token = null;
    return;
  }

  state.pendingToken = null;
  state.pendingTokenExpiresAt = null;
  state.recoveryCodes = [];

  state.token = saveImmediately ? response.token : null;
};

export const loginAdmin = createAsyncThunk(
  "adminAuth/loginAdmin",
  async (body: AdminLoginRequest, { rejectWithValue }) => {
    try {
      const response = await adminLoginRequest(body);

      if (isAdminAuthSuccessResponse(response)) {
        await saveToken(response.token);
      } else {
        await removeToken();
      }

      return response;
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

export const loadCurrentAdmin = createAsyncThunk(
  "adminAuth/loadCurrentAdmin",
  async (_, { rejectWithValue }) => {
    try {
      const token = await getToken();

      if (!token) {
        return null;
      }

      const response = await adminMeRequest();

      return {
        ...response,
        token,
      };
    } catch (error: any) {
      await removeToken();
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

export const fetchAdminTwoFactorSetup = createAsyncThunk(
  "adminAuth/fetchAdminTwoFactorSetup",
  async (setupToken: string, { rejectWithValue }) => {
    try {
      return await adminTwoFactorSetupRequest(setupToken);
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

export const confirmAdminTwoFactorSetup = createAsyncThunk(
  "adminAuth/confirmAdminTwoFactorSetup",
  async (
    payload: {
      setupToken: string;
      body: AdminTwoFactorCodeRequest;
    },
    { rejectWithValue },
  ) => {
    try {
      return await adminTwoFactorConfirmRequest(
        payload.setupToken,
        payload.body,
      );
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

export const completeAdminTwoFactorChallenge = createAsyncThunk(
  "adminAuth/completeAdminTwoFactorChallenge",
  async (
    payload: {
      challengeToken: string;
      body: AdminTwoFactorCodeRequest;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await adminTwoFactorChallengeRequest(
        payload.challengeToken,
        payload.body,
      );

      await saveToken(response.token);

      return response;
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

export const logoutAdmin = createAsyncThunk(
  "adminAuth/logoutAdmin",
  async (_, { rejectWithValue }) => {
    try {
      await adminLogoutRequest();
      await removeToken();

      return true;
    } catch (error: any) {
      await removeToken();
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

const adminAuthSlice = createSlice({
  name: "adminAuth",
  initialState,
  reducers: {
    clearAdminAuthError: (state) => {
      state.error = null;
      state.fieldErrors = {};
    },
    clearRecoveryCodes: (state) => {
      state.recoveryCodes = [];
    },
    finalizeTwoFactorSetupAfterSavingRecoveryCodes: (state) => {
      if (!state.pendingToken) {
        return;
      }

      state.token = state.pendingToken;
      state.tokenExpiresAt = state.pendingTokenExpiresAt;

      state.pendingToken = null;
      state.pendingTokenExpiresAt = null;

      state.setupToken = null;
      state.setupTokenExpiresAt = null;

      state.challengeToken = null;
      state.challengeTokenExpiresAt = null;

      state.twoFactorMode = "none";
      state.setupSecret = null;
      state.setupOtpAuthUri = null;
      state.recoveryCodes = [];

      state.loading = false;
      state.initialized = true;
      state.error = null;
      state.fieldErrors = {};
    },
    resetAdminAuthState: (state) => {
      resetSessionState(state);
      state.loading = false;
      state.initialized = true;
      state.error = null;
      state.fieldErrors = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.fieldErrors = {};
      })
      .addCase(loginAdmin.fulfilled, (state, action) => {
        state.loading = false;

        const response = action.payload as AdminLoginResponse;

        if (isAdminAuthSuccessResponse(response)) {
          applyAuthSuccess(state, response, true);
          return;
        }

        if (isAdminTwoFactorSetupRequiredResponse(response)) {
          state.user = response.user;
          state.token = null;
          state.tokenExpiresAt = null;

          state.setupToken = response.setup_token;
          state.setupTokenExpiresAt = response.expires_at;

          state.challengeToken = null;
          state.challengeTokenExpiresAt = null;

          state.twoFactorMode = "setup";
          state.setupSecret = null;
          state.setupOtpAuthUri = null;
          state.recoveryCodes = [];

          state.pendingToken = null;
          state.pendingTokenExpiresAt = null;

          state.initialized = true;
          state.error = null;
          state.fieldErrors = {};
          return;
        }

        if (isAdminTwoFactorRequiredResponse(response)) {
          state.user = response.user;
          state.token = null;
          state.tokenExpiresAt = null;

          state.challengeToken = response.challenge_token;
          state.challengeTokenExpiresAt = response.expires_at;

          state.setupToken = null;
          state.setupTokenExpiresAt = null;

          state.twoFactorMode = "challenge";
          state.setupSecret = null;
          state.setupOtpAuthUri = null;
          state.recoveryCodes = [];

          state.pendingToken = null;
          state.pendingTokenExpiresAt = null;

          state.initialized = true;
          state.error = null;
          state.fieldErrors = {};
        }
      })
      .addCase(loginAdmin.rejected, applyReject)

      .addCase(loadCurrentAdmin.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadCurrentAdmin.fulfilled, (state, action) => {
        state.loading = false;
        state.initialized = true;
        state.error = null;
        state.fieldErrors = {};

        if (!action.payload) {
          resetSessionState(state);
          return;
        }

        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(loadCurrentAdmin.rejected, (state, action) => {
        const payload = action.payload as ApiRejectPayload | undefined;

        state.loading = false;
        state.initialized = true;
        state.error = payload?.message || null;
        state.fieldErrors = {};
        resetSessionState(state);
      })

      .addCase(fetchAdminTwoFactorSetup.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.fieldErrors = {};
      })
      .addCase(fetchAdminTwoFactorSetup.fulfilled, (state, action) => {
        const response = action.payload as AdminTwoFactorSetupResponse;

        state.loading = false;
        state.setupSecret = response.secret;
        state.setupOtpAuthUri = response.otpauth_uri;
        state.error = null;
        state.fieldErrors = {};
      })
      .addCase(fetchAdminTwoFactorSetup.rejected, applyReject)

      .addCase(confirmAdminTwoFactorSetup.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.fieldErrors = {};
      })
      .addCase(confirmAdminTwoFactorSetup.fulfilled, (state, action) => {
        applyAuthSuccess(state, action.payload, false);
      })
      .addCase(confirmAdminTwoFactorSetup.rejected, applyReject)

      .addCase(completeAdminTwoFactorChallenge.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.fieldErrors = {};
      })
      .addCase(completeAdminTwoFactorChallenge.fulfilled, (state, action) => {
        applyAuthSuccess(state, action.payload, true);
      })
      .addCase(completeAdminTwoFactorChallenge.rejected, applyReject)

      .addCase(logoutAdmin.pending, (state) => {
        state.loading = true;
      })
      .addCase(logoutAdmin.fulfilled, (state) => {
        resetSessionState(state);
        state.loading = false;
        state.initialized = true;
        state.error = null;
        state.fieldErrors = {};
      })
      .addCase(logoutAdmin.rejected, (state) => {
        resetSessionState(state);
        state.loading = false;
        state.initialized = true;
      });
  },
});

export const {
  clearAdminAuthError,
  clearRecoveryCodes,
  finalizeTwoFactorSetupAfterSavingRecoveryCodes,
  resetAdminAuthState,
} = adminAuthSlice.actions;

export default adminAuthSlice.reducer;
